import React, { useEffect, useMemo, useState } from 'react';
import { createWork, deleteWork, getWorks, reorderWorks, updateWork } from '../api';
import ImageUploadField from '../components/ImageUploadField';

const sectionOptions = [
  { value: 'employment', label: '任职经历', description: '对应首页第一个 tab，适合公司/组织经历。' },
  { value: 'product', label: '产品经历', description: '对应首页第二个 tab，适合项目、产品或案例展示。' },
  { value: 'skills', label: '兴趣特长', description: '对应首页第三个 tab，适合兴趣方向、特长卡片或个人能力。' },
];

const emptyForm = {
  section: 'employment',
  title: '',
  category: '',
  description: '',
  link: '',
  image: '',
  tags: [''],
  period: '',
};

export default function AdminWorks() {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [message, setMessage] = useState('');
  const [draggingId, setDraggingId] = useState(null);

  useEffect(() => {
    fetchWorks();
  }, []);

  const worksBySection = useMemo(() => {
    return sectionOptions.map((section) => ({
      ...section,
      items: works.filter((work) => (work.section || 'employment') === section.value),
    }));
  }, [works]);

  const persistSectionOrder = async (sectionValue, orderedIds) => {
    const nextWorks = [...works];
    const sortedBySection = sectionOptions.flatMap((section) => {
      const sectionItems = nextWorks.filter((work) => (work.section || 'employment') === section.value);
      if (section.value !== sectionValue) {
        return sectionItems;
      }

      return orderedIds
        .map((id) => sectionItems.find((work) => work.id === id))
        .filter(Boolean);
    });

    setWorks(sortedBySection);
    await reorderWorks(sortedBySection.map((work) => ({ id: work.id })));
  };

  const fetchWorks = async () => {
    try {
      const res = await getWorks();
      setWorks(res.data);
    } catch (error) {
      console.error('Error fetching works:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      image: imageUrl,
    }));
  };

  const handleTagChange = (index, value) => {
    const nextTags = [...formData.tags];
    nextTags[index] = value;
    setFormData((prev) => ({
      ...prev,
      tags: nextTags,
    }));
  };

  const addTag = () => {
    setFormData((prev) => ({
      ...prev,
      tags: [...prev.tags, ''],
    }));
  };

  const removeTag = (index) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, tagIndex) => tagIndex !== index),
    }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        tags: formData.tags.map((tag) => tag.trim()).filter(Boolean),
      };

      if (editingId) {
        await updateWork(editingId, payload);
        setMessage('内容已更新！');
      } else {
        await createWork(payload);
        setMessage('内容已创建！');
      }

      resetForm();
      fetchWorks();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('保存失败');
      console.error('Error:', error);
    }
  };

  const handleEdit = (work) => {
    setEditingId(work.id);
    setFormData({
      section: work.section || 'employment',
      title: work.title || '',
      category: work.category || '',
      description: work.description || '',
      link: work.link || '',
      image: work.image || '',
      tags: work.tags?.length ? work.tags : [''],
      period: work.period || '',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除吗？')) {
      return;
    }

    try {
      await deleteWork(id);
      setMessage('内容已删除！');
      fetchWorks();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('删除失败');
      console.error('Error:', error);
    }
  };

  const handleDragStart = (id) => {
    setDraggingId(id);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  const handleDrop = async (sectionValue, targetId) => {
    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      return;
    }

    const sectionItems = works.filter((work) => (work.section || 'employment') === sectionValue);
    const orderedIds = sectionItems.map((work) => work.id);
    const fromIndex = orderedIds.indexOf(draggingId);
    const toIndex = orderedIds.indexOf(targetId);

    if (fromIndex === -1 || toIndex === -1) {
      setDraggingId(null);
      return;
    }

    orderedIds.splice(toIndex, 0, orderedIds.splice(fromIndex, 1)[0]);

    try {
      await persistSectionOrder(sectionValue, orderedIds);
      setMessage('排序已更新！');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('排序更新失败');
      console.error('Error:', error);
      fetchWorks();
    } finally {
      setDraggingId(null);
    }
  };

  const selectedSection = sectionOptions.find((option) => option.value === formData.section) || sectionOptions[0];

  if (loading) return <div className="p-8 text-white">加载中...</div>;

  return (
    <div className="p-8">
      <h2 className="text-3xl font-bold text-white mb-8">管理作品与项目</h2>

      {message && (
        <div className="mb-6 rounded-lg bg-green-900 p-4 text-green-200">
          {message}
        </div>
      )}

      <div className="mb-12 rounded-2xl border border-gray-700 bg-dark-secondary p-8">
        <h3 className="mb-3 text-xl font-bold text-white">
          {editingId ? '编辑当前内容' : '新增内容'}
        </h3>
        <p className="mb-2 text-sm text-gray-400">
          这里直接管理首页“任职经历 / 产品经历 / 兴趣特长”三个 tab 的内容。
        </p>
        <p className="mb-6 text-sm text-gray-500">
          先选择内容所属分类，再填写标题、描述、图片和标签。图片支持直接上传。
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="mb-2 block font-semibold text-white">所属分类</label>
            <select
              name="section"
              value={formData.section}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-700 bg-dark px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
            >
              {sectionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-2 text-sm text-gray-400">{selectedSection.description}</p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="mb-2 block font-semibold text-white">标题</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-700 bg-dark px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="mb-2 block font-semibold text-white">副标题 / 分类说明</label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                placeholder="例如：AI 产品设计 / 设计策略 / 个人兴趣"
                className="w-full rounded-lg border border-gray-700 bg-dark px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block font-semibold text-white">描述</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full rounded-lg border border-gray-700 bg-dark px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-2 block font-semibold text-white">跳转链接</label>
            <input
              type="text"
              name="link"
              value={formData.link}
              onChange={handleChange}
              placeholder="例如：https://example.com 或 hehaidong.de"
              className="w-full rounded-lg border border-gray-700 bg-dark px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
            />
            <p className="mt-2 text-sm text-gray-400">如果填写，首页“产品经历”卡片和详情弹窗都会支持点击跳转。</p>
          </div>

          <div>
            <label className="mb-2 block font-semibold text-white">时间 / 阶段</label>
            <input
              type="text"
              name="period"
              value={formData.period}
              onChange={handleChange}
              placeholder="例如：2024 - 至今 / 校园阶段 / 长期兴趣"
              className="w-full rounded-lg border border-gray-700 bg-dark px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
            />
          </div>

          <ImageUploadField
            label="配图"
            value={formData.image}
            onChange={handleImageChange}
            folder={formData.section}
            description="支持上传图片，也支持保留外部图片 URL。"
          />

          <div>
            <label className="mb-2 block font-semibold text-white">标签</label>
            <div className="space-y-3">
              {formData.tags.map((tag, index) => (
                <div key={`${formData.section}-${index}`} className="flex gap-2">
                  <input
                    type="text"
                    value={tag}
                    onChange={(e) => handleTagChange(index, e.target.value)}
                    placeholder="例如：UI/UX / 研究 / 足球 / 摄影"
                    className="flex-1 rounded-lg border border-gray-700 bg-dark px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                  />
                  {formData.tags.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTag(index)}
                      className="rounded-lg bg-red-900 px-3 py-3 text-red-200 hover:bg-red-800"
                    >
                      删除
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addTag}
              className="mt-3 rounded-lg bg-gray-700 px-4 py-2 text-white hover:bg-gray-600"
            >
              + 添加标签
            </button>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700"
            >
              {editingId ? '更新内容' : '创建内容'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg bg-gray-700 px-6 py-3 font-semibold text-white transition hover:bg-gray-600"
              >
                取消
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="space-y-8">
        {worksBySection.map((section) => (
          <div key={section.value}>
            <div className="mb-4 flex items-end justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">{section.label}</h3>
                <p className="mt-1 text-sm text-gray-400">{section.description}</p>
              </div>
              <span className="text-sm text-gray-500">{section.items.length} 条内容</span>
            </div>

            {section.items.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-700 bg-dark-secondary px-5 py-4 text-sm text-gray-400">
                这个分类里还没有内容，新增后就会显示在首页对应 tab。
              </div>
            ) : (
              <div className="space-y-4">
                {section.items.map((work) => (
                  <div
                    key={work.id}
                    draggable
                    onDragStart={() => handleDragStart(work.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleDrop(section.value, work.id)}
                    className={`flex items-start justify-between gap-6 rounded-xl border bg-dark-secondary p-6 ${
                      draggingId === work.id ? 'border-blue-500 opacity-70' : 'border-gray-700'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-3">
                        <span className="rounded-lg border border-gray-600 px-2 py-1 text-xs tracking-[0.2em] text-gray-400">
                          拖拽排序
                        </span>
                        <h4 className="text-lg font-bold text-white">{work.title}</h4>
                        {work.category && (
                          <span className="rounded-full border border-gray-600 px-3 py-1 text-xs text-gray-300">
                            {work.category}
                          </span>
                        )}
                      </div>
                      {work.period && <p className="text-sm text-gray-400">{work.period}</p>}
                      {work.description && (
                        <p className="mt-3 whitespace-pre-line text-sm leading-7 text-gray-300">
                          {work.description}
                        </p>
                      )}
                      {work.link && (
                        <p className="mt-3 break-all text-sm text-blue-300">
                          {work.link}
                        </p>
                      )}
                      {work.tags?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {work.tags.map((tag) => (
                            <span
                              key={`${work.id}-${tag}`}
                              className="rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-xs text-blue-200"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(work)}
                        className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => handleDelete(work.id)}
                        className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                      >
                        删除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
